// 회사 정보 칸 한 벌 — 시공사 만들기 마법사 1단계와 반려 재신청 화면이 같이 쓴다.

import { nameInputMax, PERSON_NAME_MAX } from "@buildpy/core/modules/onboarding/rules";
import { COMPANY_NAME_MAX } from "@buildpy/core/modules/org/types";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import type { WizardValues } from "./onboarding-wizard-state";

export type CompanyFieldKey = "name" | "slug" | "ceo" | "tel" | "address" | "bizNo" | "licenseNo";

interface Props {
  values: Pick<WizardValues, CompanyFieldKey>;
  pending: boolean;
  onChange: (patch: Partial<Pick<WizardValues, CompanyFieldKey>>) => void;
  // 재신청은 간편접수 주소를 바꾸지 않는다.
  slugLocked?: boolean;
}

const CompanyFields = ({ values: v, pending, onChange, slugLocked = false }: Props) => {
  const text = (key: CompanyFieldKey) => ({
    name: key,
    value: v[key],
    onChange: (e: { target: { value: string } }) => onChange({ [key]: e.target.value }),
    disabled: pending,
  });
  return (
    <>
      <div className="flex flex-col gap-y-4">
        <div className="flex flex-col gap-y-1.5">
          <Label htmlFor="onboarding-name">상호 *</Label>
          <Input autoComplete="organization" id="onboarding-name" maxLength={COMPANY_NAME_MAX} required {...text("name")} />
        </div>
        <div className="flex flex-col gap-y-1.5">
          <Label htmlFor="onboarding-slug">간편접수 주소 *</Label>
          <div className="flex items-center gap-x-2">
            <span className="shrink-0 text-sm text-muted-foreground">/s/intake/</span>
            <Input autoCapitalize="none" id="onboarding-slug" maxLength={40} placeholder="예: hanbit-interior" required {...text("slug")} disabled={pending || slugLocked} />
          </div>
          <p className="text-xs text-muted-foreground">
            {slugLocked ? "간편접수 주소는 다시 신청할 때 바꿀 수 없습니다" : "고객이 견적을 문의하는 주소입니다 · 소문자·숫자·하이픈 3~40자"}
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-y-1.5">
            <Label htmlFor="onboarding-ceo">대표자명 *</Label>
            <Input autoComplete="name" id="onboarding-ceo" maxLength={nameInputMax(PERSON_NAME_MAX)} required {...text("ceo")} />
          </div>
          <div className="flex flex-col gap-y-1.5">
            <Label htmlFor="onboarding-tel">대표 전화 *</Label>
            <Input autoComplete="tel" id="onboarding-tel" inputMode="tel" maxLength={20} placeholder="02-1234-5678" required type="tel" {...text("tel")} />
          </div>
        </div>
      </div>
      <fieldset className="flex flex-col gap-y-4 rounded-md border border-border p-4">
        <legend className="px-1 text-sm font-medium">계약서에 들어가는 정보 · 선택 · 나중에 넣어도 됩니다</legend>
        <div className="flex flex-col gap-y-1.5">
          <Label htmlFor="onboarding-address">사업장 주소</Label>
          <Input autoComplete="street-address" id="onboarding-address" maxLength={200} {...text("address")} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-y-1.5">
            <Label htmlFor="onboarding-bizno">사업자등록번호</Label>
            <Input id="onboarding-bizno" inputMode="numeric" maxLength={12} placeholder="000-00-00000" {...text("bizNo")} />
          </div>
          <div className="flex flex-col gap-y-1.5">
            <Label htmlFor="onboarding-license">실내건축공사업 등록번호</Label>
            <Input id="onboarding-license" maxLength={40} {...text("licenseNo")} />
          </div>
        </div>
      </fieldset>
    </>
  );
};

export default CompanyFields;
